import SearchIcon from "@mui/icons-material/Search";
import {
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  Skeleton,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { Dialog } from "components/Dialog";
import { useDebounce } from "hooks/useDebounce";
import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { TextInput } from "routes/Customer/Tasks/TextInput";

import { SEEDED_THREAD_SUMMARIES, type ScoutThreadSummary } from "../hooks/useScoutChatWithMocks";

const SEARCH_DEBOUNCE_MS = 400;

type Props = {
  open: boolean;
  onClose: () => void;
};

export function SearchThreadsDialog({ open, onClose }: Props) {
  const navigate = useNavigate();
  const { tenantId } = useParams();
  const [searchInput, setSearchInput] = useState("");
  const trimmed = searchInput.trim();
  const [debouncedSearch, isSearchDebouncing] = useDebounce(trimmed, SEARCH_DEBOUNCE_MS);

  const recents = useRecentThreads();
  const searchResults = useSearchResults(debouncedSearch);

  const showRecents = trimmed.length === 0;
  const showSearchLoading = trimmed.length > 0 && isSearchDebouncing;

  const handleThreadSelect = (threadId: string) => {
    navigate(`/o/${tenantId}/scout/${threadId}`);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      noPrimaryAction
      DialogContentProps={{ sx: { paddingBottom: 2 } }}
      title={
        <TextInput
          placeholder="Search threads..."
          fullWidth
          autoFocus
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          startAdornment={
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          }
        />
      }
    >
      {showRecents ? (
        <ThreadListSection
          heading="Recents"
          items={recents}
          loading={false}
          onSelect={handleThreadSelect}
        />
      ) : (
        <ThreadListSection
          heading="Search results"
          items={searchResults}
          loading={showSearchLoading}
          onSelect={handleThreadSelect}
        />
      )}
    </Dialog>
  );
}

function ThreadListSection({
  heading,
  items,
  loading,
  onSelect,
}: {
  heading: string;
  items: ScoutThreadSummary[];
  loading?: boolean;
  onSelect: (id: string) => void;
}) {
  const theme = useTheme();
  return (
    <Stack spacing={1} paddingBottom={theme.spacing(1)} sx={{ maxHeight: 200, overflow: "auto" }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="caption">{heading}</Typography>
      </Stack>
      <List disablePadding>
        {loading ? (
          Array.from({ length: 3 }, (_, i) => (
            <ListItemButton key={i}>
              <Skeleton variant="text" width="100%" height={32} />
            </ListItemButton>
          ))
        ) : (
          <>
            {items.map((item) => (
              <ListItemButton
                key={item.id}
                onClick={() => onSelect(item.id)}
                sx={{
                  width: "100%",
                  borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
                  "&:last-child": {
                    borderBottom: "none",
                  },
                }}
              >
                <ListItemText primary={item.title} />
              </ListItemButton>
            ))}
          </>
        )}
      </List>
    </Stack>
  );
}

// TODO: replace SEEDED_THREAD_SUMMARIES with the real recents query once the Scout conversations API lands.
const useRecentThreads = (): ScoutThreadSummary[] => {
  return SEEDED_THREAD_SUMMARIES;
};

// TODO: replace SEEDED_THREAD_SUMMARIES with the real search query once the Scout conversations API lands.
const useSearchResults = (search: string): ScoutThreadSummary[] => {
  if (search.length === 0) {
    return [];
  }
  const lower = search.toLowerCase();
  return SEEDED_THREAD_SUMMARIES.filter((t) => t.title.toLowerCase().includes(lower));
};
